import {buildContract} from '../blueprint/contract';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Quote, QuoteStatus, Product } from '../types';
import { Button } from './ui/Button';
import { GlassCard } from './ui/GlassCard';
import { Share2, Check, X, ArrowLeft, Edit3, Mail, Phone, Calendar, MapPin, Clock, FileText, ChevronLeft, ChevronRight, ChevronDown, Layout, Maximize2, Minimize2, Download, CheckCircle, ArrowRight, Send, Plus, PlusCircle, Tag, FilePlus, Monitor, User } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { clientView, clientRoute, clientRpc, issueClientLink } from '../clientAccess';
import { motion, AnimatePresence } from 'framer-motion';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import { calculationPatch, displayQuotePricing, includedQuoteItems, isAcceptedQuote, linePrice, quoteItems, quoteStaffing } from '../quoteCalculations';
import { approveClientQuote, ClientApprovalError, currentQuoteApprovalState, requiresSavedCalculationReview, reviewSavedQuoteForClient } from '../clientQuoteApproval';
import { displayValidityDate, nzDateAfterDays, quoteValidUntil, renewQuoteValidity } from '../quoteValidity';
import { canSupersedeQuote, isSupersededQuote } from '../quoteSuperseded';

const QUOTE_FONT_FACES = [
  '400 16px "Anton"',
  '400 16px "Inter"',
  '600 16px "Inter"',
  '700 16px "Inter"',
];

const PRESENTATION_CANVAS_WIDTH = 1920;
const PRESENTATION_CANVAS_HEIGHT = 1080;
const PRESENTATION_MIN_WIDTH = 768;
const PRESENTATION_TOOLBAR_HEIGHT = 76;
const PRESENTATION_CONTROLS_HEIGHT = 80;

const getViewportSize = () => ({
  width: typeof window === 'undefined' ? PRESENTATION_CANVAS_WIDTH : window.innerWidth,
  height: typeof window === 'undefined' ? PRESENTATION_CANVAS_HEIGHT : window.innerHeight,
});

export const ensureQuoteFontsReady = async (fontSet: FontFaceSet = document.fonts) => {
  await Promise.all(QUOTE_FONT_FACES.map((face) => fontSet.load(face)));
  await fontSet.ready;

  const missingFaces = QUOTE_FONT_FACES.filter((face) => !fontSet.check(face));
  if (missingFaces.length > 0) {
    throw new Error('The QuikPix proposal fonts could not be loaded. Please check your connection and try again.');
  }
};

const waitForExportLayout = async () => {
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
};

interface QuotePreviewProps {
  quote: Quote;
  onStatusChange?: (status: QuoteStatus) => void;
  onUpdate?: (quote: Quote) => void;
  onBack: () => void;
  onEdit?: () => void;
  onPreview?: () => void;
  isPublicView?: boolean;
  availableProducts?: Product[];
  onDuplicateQuote?: (quote: Quote) => void;
}

export const QuotePreview: React.FC<QuotePreviewProps> = ({
  quote,
  onStatusChange,
  onUpdate,
  onBack,
  onEdit,
  isPublicView = false,
  availableProducts = [],
  onDuplicateQuote
}) => {
  const [viewportSize, setViewportSize] = useState(getViewportSize);
  const isMobileDevice = viewportSize.width < PRESENTATION_MIN_WIDTH;
  const [viewMode, setViewMode] = useState<'PRESENTATION' | 'DOCUMENT'>(isPublicView && !isMobileDevice ? 'PRESENTATION' : 'DOCUMENT');
  const [showMobileNotice, setShowMobileNotice] = useState(isPublicView && isMobileDevice);

  const [currentStep, setCurrentStep] = useState(0);
  const [isSharing, setIsSharing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const approvalInFlight = useRef(false);
  const [isApproving, setIsApproving] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [reviewReady, setReviewReady] = useState(false);
  const [reviewGeneration, setReviewGeneration] = useState(0);
  const [approvalState, setApprovalState] = useState<any>(null);
  const [canRenewValidity, setCanRenewValidity] = useState(false);
  const [validityOpen, setValidityOpen] = useState(false);
  const [newValidUntil, setNewValidUntil] = useState('');
  const [renewingValidity, setRenewingValidity] = useState(false);
  const [validityError, setValidityError] = useState('');
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const [presentationHeaderHeight, setPresentationHeaderHeight] = useState(PRESENTATION_TOOLBAR_HEIGHT);

  useEffect(() => {
    const header = previewHeaderRef.current;
    if (!header || viewMode !== 'PRESENTATION') return;
    const measure = () => setPresentationHeaderHeight(Math.max(PRESENTATION_TOOLBAR_HEIGHT, header.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [viewMode]);
  
  const [selectedUpgrades, setSelectedUpgrades] = useState<Set<string>>(new Set());

  const presentationAvailableHeight = Math.max(
    1,
    viewportSize.height - presentationHeaderHeight - PRESENTATION_CONTROLS_HEIGHT,
  );
  const presentationScale = Math.min(
    viewportSize.width / PRESENTATION_CANVAS_WIDTH,
    presentationAvailableHeight / PRESENTATION_CANVAS_HEIGHT,
  );

  useEffect(() => {
    const updateViewportSize = () => setViewportSize(getViewportSize());
    window.addEventListener('resize', updateViewportSize);
    return () => window.removeEventListener('resize', updateViewportSize);
  }, []);

  useEffect(() => {
    if (!isMobileDevice) return;
    setViewMode('DOCUMENT');
    if (isPublicView) setShowMobileNotice(true);
  }, [isMobileDevice, isPublicView]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAdminPreview = urlParams.get('admin') === 'true';

    if (isPublicView && !isAdminPreview && !isAcceptedQuote(quote) && quote?.id && !quote.id.startsWith('draft-')) {
      // Optional view analytics do not require anonymous quote writes.

    }
  }, [isPublicView, quote?.id, quote?.status]);

  useEffect(() => {
    if (quote) {
        const isPortfolioLayout = (quote as any).is_template || (quote as any).is_portfolio_mode || false;
        if (isPortfolioLayout && !isMobileDevice) {
            setViewMode('PRESENTATION');
        }
    }
  }, [quote, isMobileDevice]);

  const allItems = quoteItems(quote?.items);
  const mandatoryItems = allItems.filter(i => !i.isOptional);
  const optionalItems = allItems.filter(i => i.isOptional);

  const pricing = displayQuotePricing(quote, selectedUpgrades);
  const totalGrossItems = pricing.gross;
  const totalVIPDiscount = pricing.discount;
  const savedReviewRequired = requiresSavedCalculationReview(quote);
  const calculationReviewRequired = !isPublicView && !isSupersededQuote(quote) && savedReviewRequired && !reviewReady;
  useEffect(() => {
    let cancelled = false;
    setReviewReady(false);
    setApprovalState(null);
    setCanRenewValidity(false);
    if (!quote?.id || quote.id.startsWith('draft-') || !['DRAFT','SENT'].includes(String(quote.status).toUpperCase())) return;
    if (!isPublicView) void supabase.rpc('crew_identity').then(({ data }: any) => { if (!cancelled) setCanRenewValidity(data?.role === 'Admin'); });
    void currentQuoteApprovalState(quote.id).then(state => { if (!cancelled) {
      setApprovalState(state);
      setReviewReady(state?.calculation_reviewed === true || !!state?.ready);
    } })
      .catch(error => console.error('Quote approval readiness check failed', { quoteId: quote.id, code: error?.code, message: error?.message }));
    return () => { cancelled = true; };
  }, [quote, savedReviewRequired, reviewGeneration, isPublicView]);
  const validUntil = approvalState?.valid_until ?? quoteValidUntil(quote);
  const expired = approvalState?.reason === 'EXPIRED';
  const saveValidity = async (event: React.FormEvent) => {
    event.preventDefault();
    if (renewingValidity) return;
    setRenewingValidity(true); setValidityError('');
    try {
      await renewQuoteValidity(quote.id, newValidUntil);
      setValidityOpen(false); setReviewGeneration(value => value + 1);
      alert('Quote validity renewed. Prices and calculation review are unchanged.');
    } catch (error: any) { setValidityError(error?.message || 'Could not renew quote validity.'); }
    finally { setRenewingValidity(false); }
  };
  const reviewCalculation = async () => {
    try {
      if (await reviewSavedQuoteForClient(quote.id)) {
        setReviewGeneration(value => value + 1);
        alert('The saved calculation has been explicitly reviewed. Quote validity must also be current before client approval.');
      }
    } catch (error: any) { alert(error?.message || 'Calculation review could not be recorded.'); }
  };
  
  // ⚡ BULLETPROOF TRANSLATOR: Forces the visual preview to respect your zero!
  let parsedLogistics: any = quote?.logistics || {};
  if (typeof parsedLogistics === 'string') {
      try { parsedLogistics = JSON.parse(parsedLogistics); } catch (e) { parsedLogistics = {}; }
  }

  let finalSetup = 0;
  if (parsedLogistics.setupFee !== undefined && parsedLogistics.setupFee !== null && parsedLogistics.setupFee !== '') {
      finalSetup = Number(parsedLogistics.setupFee);
  } else {
      finalSetup = Number((quote as any)?.setup_fee || 0);
  }

  const safeLogistics = {
    hours: 0,
    hourlyRate: 0,
    travelDistanceKm: Number(parsedLogistics.travelDistanceKm || (quote as any)?.travel_distance_km || 0),
    travelRatePerKm: Number(parsedLogistics.travelRatePerKm || (quote as any)?.travel_rate_per_km || 0),
    setupFee: finalSetup,
    // ⚡ ADD THIS LINE:
    roadshowSchedule: parsedLogistics.roadshowSchedule || ''
  };

  const logisticsCost = 0;

  const subtotal = pricing.subtotal;
  const gst = pricing.gst;
  const total = pricing.total;

  const toggleUpgrade = (itemId: string) => {
      if (isAcceptedQuote(quote)) return;
      const newSet = new Set(selectedUpgrades);
      if (newSet.has(itemId)) {
          newSet.delete(itemId);
      } else {
          newSet.add(itemId);
      }
      setSelectedUpgrades(newSet);
  };

  const quoteRef = (quote as any).quote_number || (quote as any).quoteNumber || 'PENDING';
  const displayCompany = (quote as any).client_company || (quote as any).clientCompany || quote.client?.company || 'Private Account';

  const safeFirstName = quote?.client?.firstName || (quote as any)?.client_first_name || '';
  const safeLastName = quote?.client?.lastName || (quote as any)?.client_last_name || '';
  const safeEmail = quote?.client?.email || (quote as any)?.client_email || '';
  const safePhone = quote?.client?.phone || (quote as any)?.client_phone || '';
  const safeEventDate = quote?.eventDate || (quote as any)?.event_date;
  const safeEventLocation = quote?.eventLocation || (quote as any)?.event_location;
  
  // ⚡ DYNAMIC STAFFING MATH FOR THE UI ⚡
  const safeStaffCount = quoteStaffing(quote, includedQuoteItems(allItems, selectedUpgrades)).total;
  const rawStaffingTier = (quote as any)?.staffing_tier || (quote as any)?.staffingTier || 'DRY_HIRE';
  const safeStaffingTier = safeStaffCount > 0 ? 'FULLY_STAFFED' : rawStaffingTier;
  
  const formatTime = (timeStr: string | null | undefined) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const safeStartTime = formatTime(quote?.eventStartTime || (quote as any)?.event_start_time);
  const safeEndTime = formatTime(quote?.eventEndTime || (quote as any)?.event_end_time);
  const safeSetupTime = formatTime(quote?.setupTime || (quote as any)?.setup_time);
  const safePackTime = formatTime(quote?.packDownTime || (quote as any)?.pack_down_time);
  const safeCreatedAt = quote?.createdAt || (quote as any)?.created_at;

  const isTemplate = (quote as any)?.is_template || false;

  const proposalSlides = useMemo(() => {
    const slides: { id: string; type: 'COVER' | 'INTRO' | 'PRODUCT' | 'PRICING' | 'ACCEPT' | 'CUSTOM' | 'PORTFOLIO_CTA'; data?: any }[] = [];

    const customSlides = (quote as any)?.custom_slides || [];
    const introSlides = customSlides.filter((s: any) => s.placement === 'intro');
    const strategySlides = customSlides.filter((s: any) => s.placement === 'strategy');
    const intermissionSlides = customSlides.filter((s: any) => s.placement === 'intermission');
    const appendixSlides = customSlides.filter((s: any) => s.placement === 'appendix');

    slides.push({ id: 'cover', type: 'COVER' });
    introSlides.forEach((slide: any, idx: number) => slides.push({ id: `intro-custom-${idx}`, type: 'CUSTOM', data: slide }));

    const rawIntro = (quote as any)?.presentation_slide_content || quote?.presentationSlideContent;
    const introText = typeof rawIntro === 'object' && rawIntro !== null ? rawIntro.text : rawIntro;

    if (introText && String(introText).trim() !== "") {
      slides.push({ id: 'intro', type: 'INTRO', data: { text: introText } });
    }

    strategySlides.forEach((slide: any, idx: number) => {
        slides.push({ id: `strategy-custom-${idx}`, type: 'CUSTOM', data: slide });
    });

    (quote?.items || []).forEach((item, idx) => {
        const pId = item?.productId || (item as any)?.product_id;
        if (pId) {
            const product = availableProducts.find(p => String(p.id) === String(pId));
            if (product) {
                const heroVisual = product.image || (product as any).main_image;
                if (heroVisual) {
                    slides.push({
                        id: `prod-${idx}-main`,
                        type: 'PRODUCT',
                        data: { product: product, slide: { type: 'image', url: heroVisual, title: product.name, caption: product.description }, item: item }
                    });
                }

                const extraSlides = (product as any).presentation_slides || product.presentationSlides || [];
                if (extraSlides.length > 0) {
                    extraSlides.forEach((slide: any, sIdx: number) => {
                        slides.push({
                            id: `prod-${idx}-extra-${sIdx}`,
                            type: 'PRODUCT',
                            data: { product: product, slide: { ...slide, caption: slide.description || slide.caption || '' }, item: item }
                        });
                    });
                }
            }
        }
    });

    intermissionSlides.forEach((slide: any, idx: number) => slides.push({ id: `inter-custom-${idx}`, type: 'CUSTOM', data: slide }));

    if (quote?.items && quote.items.length > 0) {
      slides.push({ id: 'pricing', type: 'PRICING' });
    }

    appendixSlides.forEach((slide: any, idx: number) => slides.push({ id: `app-custom-${idx}`, type: 'CUSTOM', data: slide }));

    if (isTemplate) {
        slides.push({ id: 'portfolio-cta', type: 'PORTFOLIO_CTA' });
    } else {
        slides.push({ id: 'accept', type: 'ACCEPT' });
    }

    return slides;
  }, [quote, availableProducts, isTemplate]);

  const handleNext = () => {
    if (currentStep < proposalSlides.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (viewMode === 'PRESENTATION') {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, viewMode]);

  // ⚡ ENGINE 1: A4 PORTRAIT FORMAL QUOTE
  const handleDownloadQuote = async () => {
    setViewMode('DOCUMENT');
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await ensureQuoteFontsReady();
      await waitForExportLayout();
      const element = document.getElementById('quote-print-container');
      if (!element) return;

      const opt: any = {
        margin:       [15, 10, 15, 10],
        filename:     `Quikpix_Quote_${quoteRef}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { 
            scale: 2, 
            useCORS: true,
            windowWidth: 1024
        },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:    { mode: ['css', 'legacy'] } 
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Quote PDF export failed:', error);
      alert(error instanceof Error ? error.message : 'The quote PDF could not be generated. Please try again.');
    }
  };

  // ⚡ ENGINE 2: 16:9 LANDSCAPE PRESENTATION DECK
  const handleDownloadDeck = async () => {
    try {
      await ensureQuoteFontsReady();
      await waitForExportLayout();
      const element = document.getElementById('presentation-print-container');
      if (!element) return;

    const opt: any = {
      margin:       0, 
      filename:     `Quikpix_Presentation_${quoteRef}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { 
          scale: 1.5, 
          useCORS: true,
          windowWidth: 1920,
          windowHeight: 1080
      },
      jsPDF:        { unit: 'px', format: [1920, 1080], orientation: 'landscape' }, 
      pagebreak:    { mode: ['css', 'legacy'] } 
    };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Presentation PDF export failed:', error);
      alert(error instanceof Error ? error.message : 'The presentation PDF could not be generated. Please try again.');
    }
  };

  const generatePublicLink = async () => isPublicView
    ? `${window.location.origin}/${clientRoute('view-quote',quote.id)}` : issueClientLink(quote.id,safeEmail);
  const handleShare = async () => {
    try { const url=await generatePublicLink(); await navigator.clipboard.writeText(url); setIsSharing(true); setTimeout(()=>setIsSharing(false),2000); }
    catch { alert('Could not issue a secure client link. Please contact Quikpix.'); }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      if (quote?.id && !quote.id.startsWith('draft-')) {
        await supabase.from('quotes').update({ status: 'SENT' }).eq('id', quote.id);
        const leadId = quote.linkedCrmItemId || (quote as any).lead_id;
        if (leadId) {
            await supabase.from('event_leads').update({ status: 'PROPOSAL' }).eq('id', leadId);
        }
      }
      if (onUpdate) onUpdate({ ...quote, status: 'SENT' });
      
      const url = await generatePublicLink();
      alert(`Quote marked as SENT! \n\nClean Link copied to clipboard.`);
      navigator.clipboard.writeText(url);
    } catch (e) {
      console.error("Error sending quote", e);
    } finally {
      setIsSending(false);
    }
  };

  const handleApprove = async () => {
    if (isSupersededQuote(quote)) return;
    if (isAcceptedQuote(quote) || approvalInFlight.current) return;
    if (isPublicView && (!signatureOpen || !termsAgreed || signerName.trim().length < 2)) {
      setSignatureOpen(true);
      return;
    }
    approvalInFlight.current = true;
    setIsApproving(true);
    setApprovalError('');
    try {
      if (!quote?.id || quote.id.startsWith('draft-')) return;

      // Public acceptance uses the persisted quote in the server transaction.
      // Browser calculations are only a display preview and never trusted prices.
      const calculated: ReturnType<typeof calculationPatch> = isPublicView ? {
        items: quoteItems(quote.items).map(item => ({ ...item, isOptional: selectedUpgrades.has(item.id) ? false : item.isOptional })),
        subtotal: pricing.subtotal, grand_total: pricing.total,
        staff_count_required: quoteStaffing(quote).total, staffing_tier: (quote as any).staffing_tier,
        total_amount: pricing.total, gst_amount: pricing.gst, logistics: quote.logistics,
      } : calculationPatch(quote, selectedUpgrades);
      const finalItemsToSave = calculated.items;
      const absoluteFinalSubtotal = calculated.subtotal;
      const absoluteFinalTotal = calculated.grand_total;

      const finalBlueprint = buildContract(finalItemsToSave,availableProducts.map(p=>({...p,onboarding_fields:(p as any).onboarding_fields || p.onboardingFields || []}))).questions.map(({key,category,...field})=>field);

      const allProductGear = quote.items.flatMap(item => {
        const p = availableProducts.find(p => String(p.id) === String(item.productId || (item as any).product_id));
        return p ? ((p as any).equipment_list || (p as any).equipmentList || []) : [];
      });
    
      const originalPackingList = quote.packingList || (quote as any).packing_list || [];
      const customGear = originalPackingList.filter((item: string) => !allProductGear.includes(item));
    
      const purchasedGear = finalItemsToSave
        .filter(item => !item.isOptional)
        .flatMap(item => {
            const p = availableProducts.find(p => String(p.id) === String(item.productId || (item as any).product_id));
            return p ? ((p as any).equipment_list || (p as any).equipmentList || []) : [];
        });
        
      const finalPackingList = Array.from(new Set([...customGear, ...purchasedGear]));

      if (isPublicView) {
        const result = await approveClientQuote(quote, signerName.trim(), selectedUpgrades, finalBlueprint, finalPackingList);
        const { quote: persisted } = await clientView(quote.id);
        if (onUpdate) await onUpdate({ ...quote, ...(persisted || {}), status: result.status });
        setSignatureOpen(false);
        alert('Thank you! Your quote has been approved. Next, please complete your Event Blueprint.');
        window.location.hash = clientRoute('portal',quote.id);
        return;
      }

      const { error: quoteError } = await supabase.from('quotes').update({
        ...calculated,
        status: 'APPROVED',
        subtotal: absoluteFinalSubtotal,
        grand_total: absoluteFinalTotal,
        items: finalItemsToSave,
        portal_blueprint: finalBlueprint,
        packing_list: finalPackingList,
      }).eq('id', quote.id);

      if (quoteError) throw quoteError;

      if (onUpdate) await onUpdate({ ...quote, ...calculated, staffCountRequired: calculated.staff_count_required, staffingTier: calculated.staffing_tier, status: 'APPROVED', subtotal: absoluteFinalSubtotal, grandTotal: absoluteFinalTotal, items: finalItemsToSave, packingList: finalPackingList, packing_list: finalPackingList, equipmentList: finalPackingList } as Quote);
      else if (onStatusChange) onStatusChange('APPROVED');

      alert("Success! The quote has been approved and the Event Blueprint has been generated.");
    } catch (e) {
      console.error("Critical error in approval flow:", e);
      const message = (isPublicView ? e instanceof ClientApprovalError : e instanceof Error)
        ? (e as Error).message : "We couldn't complete the approval. Please try again or contact Quikpix.";
      if (isPublicView) setApprovalError(message);
      else alert(message);
    } finally {
      approvalInFlight.current = false;
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      if (quote?.id && !quote.id.startsWith('draft-')) {
        if(isPublicView) await clientRpc('client_quote_decline_authorized',quote.id,{p_expected_updated_at:(quote as any).updated_at ?? null});
        else await supabase.from('quotes').update({ status: 'REJECTED' }).eq('id', quote.id);
      }
      if (onUpdate) onUpdate({ ...quote, status: 'REJECTED' });
      else if (onStatusChange) onStatusChange('REJECTED');
    } catch (e) {
      console.error(e);
    }
  };

  const CoverSlide = () => (
    <div className="h-full flex flex-col items-center justify-center relative overflow-hidden bg-slate-950">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-neonBlue/10 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-brand-neonPink/10 rounded-full blur-[120px] animate-pulse-slow" style={{animationDelay: '1s'}}></div>
      </div>
      
      <div className="relative z-10 text-center max-w-4xl w-full px-4 animate-fade-in-up">
        <div className="mb-8 inline-block">
          <div className="h-1 w-24 bg-gradient-to-r from-brand-neonBlue to-brand-neonPink mx-auto rounded-full mb-8"></div>
        </div>
        <h1 className="type-display text-7xl text-white mb-6 leading-[0.98]">
        {quote?.title} <span className="text-slate-500 text-3xl block mt-2">#Q-{quoteRef}</span>
        </h1>
        
        {(safeFirstName || safeLastName) && (
            <p className="text-2xl text-slate-300 font-light mb-8">
                Prepared for <span className="text-white font-medium">{safeFirstName} {safeLastName}</span>
                {displayCompany !== 'Private Account' && <span className="text-brand-neonBlue font-bold block mt-2">{displayCompany}</span>}
            </p>
        )}
        
        {!isTemplate && (
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400 uppercase tracking-widest mt-6">
            {/* ⚡ SMART DATE TOGGLE (COVER SLIDE) */}
            {safeLogistics.roadshowSchedule ? (
                <span className="flex items-center gap-2 text-brand-neonBlue font-bold">
                    <Calendar size={16}/> Starts: {safeEventDate ? new Date(safeEventDate).toLocaleDateString() : 'TBD'}
                </span>
            ) : (
                <span className="flex items-center gap-2">
                    <Calendar size={16}/> {safeEventDate ? new Date(safeEventDate).toLocaleDateString() : 'TBD'}
                </span>
            )}
            <span className="flex items-center gap-2"><MapPin size={16}/> {safeEventLocation || 'Venue TBD'}</span>
            {(safeStartTime || safeEndTime) && (
                <span className="flex items-center gap-2 text-brand-neonBlue font-bold">
                    <Clock size={16}/> 
                    {safeStartTime || 'TBD'} {safeEndTime ? `- ${safeEndTime}` : ''}
                </span>
            )}
            </div>
        )}
        
        <div className="mt-12">
          <Button size="lg" onClick={handleNext} className="group">
            View Proposal <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform"/>
          </Button>
        </div>
      </div>
    </div>
  );

  const IntroSlide = ({ text }: { text: string }) => (
    <div className="h-full flex items-center justify-center bg-slate-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-neonBlue/5 rounded-full blur-[100px]" />
      <div className="max-w-6xl px-8 w-full relative z-10 py-12 flex flex-col justify-center min-h-full">
        
        <div className="flex flex-row gap-12 items-start">
          <div className="w-2/3 space-y-8 animate-fade-in">
            <div className="space-y-2 flex justify-between items-end">
              <div>
                <h4 className="text-brand-neonBlue font-bold text-xs uppercase tracking-[0.3em]">The Proposal</h4>
                <h2 className="type-display text-4xl text-white">Executive Vision</h2>
              </div>
            </div>
            <p className="text-xl text-slate-300 leading-relaxed whitespace-pre-wrap font-light italic border-l-4 border-brand-neonBlue/20 pl-6">
              {text}
            </p>
          </div>
          
          <div className="w-1/3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <GlassCard className="p-8 border-white/10 bg-slate-950/40 space-y-6 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="p-3 bg-brand-neonBlue/10 rounded-xl text-brand-neonBlue">
                  <Check size={24} />
                </div>
                <span className="type-numeric text-slate-500 text-xs">EST. 2004</span>
              </div>
              <h3 className="type-heading text-xl text-white">The Quikpix Edge</h3>
              <div className="space-y-4 text-[11px] text-slate-400">
                <p>• <strong>20+ Years Excellence:</strong> Delivering premium event experiences across NZ.</p>
                <p>• <strong>Custom Tech:</strong> Proprietary software and high-end hardware for flawless execution.</p>
                <p>• <strong>Reliability:</strong> Thousands of successful events backed by 24/7 expert support.</p>
              </div>
            </GlassCard>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-2">
              <p className="text-slate-400 text-sm italic leading-relaxed">
                "We've worked with Quikpix on a number of sponsorship activations and we thoroughly enjoy collaborating with them. The team are professional and fantastic to work alongside."
              </p>
              <p className="text-brand-neonBlue font-bold text-[10px] uppercase tracking-widest">— Chantelle Banbury, Canon NZ</p>
            </div>
            <div className="space-y-2">
              <p className="text-slate-400 text-sm italic leading-relaxed">
                "We had an outstanding experience with Quikpix! Their AI Photobooth was a huge hit, and the team couldn’t have been more professional or creative. From the start, they were easy to communicate with and made sure every detail matched our requirements perfectly."
              </p>
              <p className="text-brand-neonPink font-bold text-[10px] uppercase tracking-widest">— Victoria T., University of Auckland</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  const ProductSlide = ({ data }: { data: any }) => {
    const product = data.product;
    const activeMedia = data.slide?.url || data.url || product?.image || product?.main_image;
    const isHeroSlide = activeMedia === (product?.image || product?.main_image);

    const isVideo = activeMedia && (
        activeMedia.toLowerCase().endsWith('.mp4') || 
        activeMedia.toLowerCase().endsWith('.webm') || 
        activeMedia.toLowerCase().endsWith('.mov') ||
        activeMedia.includes('video')
    );
  
    return (
      <div className="flex flex-row h-full w-full bg-slate-950 overflow-hidden">
        <div className={`${isHeroSlide ? 'w-2/3' : 'w-full'} h-full relative transition-all duration-700 ease-in-out bg-black shrink-0`}>
          {activeMedia ? (
            isVideo ? (
              <video src={activeMedia} className="w-full h-full object-cover" autoPlay muted loop playsInline />
            ) : (
              <img src={activeMedia} alt={product?.name || 'Service Visual'} className="w-full h-full object-contain" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-900 text-slate-600 font-sans">
               [MEDIA DATA MISSING]
            </div>
          )}
          
          {!isHeroSlide && data.slide?.caption && (
            <div className="absolute bottom-32 left-10 bg-black/80 backdrop-blur-md p-6 rounded-xl border border-white/10 max-w-2xl z-20 shadow-2xl">
               <p className="text-white text-xl font-medium leading-relaxed">{data.slide.caption}</p>
            </div>
          )}
        </div>
  
        {isHeroSlide && (
          <div className="w-1/3 h-full bg-slate-900/95 backdrop-blur-2xl border-l border-white/10 p-12 flex flex-col justify-between overflow-y-auto custom-scrollbar shrink-0">
            <div className="animate-fade-in">
              <div className="flex items-center gap-2 mb-8">
                <div className="h-1 w-12 bg-brand-neonBlue"></div>
                <span className="text-brand-neonBlue font-bold tracking-[0.2em] text-[10px] uppercase">Included Service</span>
              </div>
  
              <h2 className="type-display text-4xl text-white mb-4 leading-[1.02]">{product?.name}</h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed italic opacity-80">{product?.description}</p>
              
              {product?.long_description && (
                <div className="text-slate-400 mb-10 leading-relaxed text-sm space-y-4 border-l-2 border-white/5 pl-4">
                  {product.long_description.split('\n').map((para: string, i: number) => (
                      para.trim() && <p key={i}>{para}</p>
                  ))}
                </div>
              )}
  
              {product?.features && product.features.length > 0 && (
                <div className="space-y-4 pt-8 border-t border-white/5">
                  <h4 className="text-white font-bold text-[10px] uppercase tracking-[0.3em] mb-4 opacity-50">Core Features</h4>
                  {product.features.map((feature: string, i: number) => (
                    <div key={i} className="flex items-start gap-3 text-slate-200">
                      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-neonBlue shrink-0 shadow-[0_0_10px_rgba(0,243,255,1)]" />
                      <span className="text-sm font-semibold tracking-wide">{feature}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
  
            <div className="pt-8 mt-8 border-t border-white/5">
               <span className="block text-slate-500 text-[10px] uppercase font-bold mb-1 tracking-widest">Quantity</span>
               <span className="text-3xl text-white font-bold">x {data.item?.quantity || data.quantity || 1} Unit(s)</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const PricingSlide = () => (
    <div className="h-full w-full bg-slate-900 overflow-y-auto overflow-x-hidden custom-scrollbar p-8 flex justify-center">
      <div className="max-w-4xl w-full my-auto py-12">
          
        <div className="text-center mb-8">
            <h2 className="type-display text-4xl text-white mb-4">Investment Summary</h2>
            <p className="text-slate-400">A clear breakdown of your core services and available upgrades.</p>
        </div>

        {/* ⚡ NEW: EVENT SNAPSHOT BAR */}
        {!isTemplate && (
            <div className="bg-slate-950/50 border border-white/10 rounded-2xl p-4 md:p-6 mb-8 flex flex-wrap items-center justify-center gap-4 md:gap-8 shadow-inner animate-fade-in-up">
                <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                    <Calendar size={16} className="text-brand-neonBlue shrink-0" />
                    {/* ⚡ SMART DATE TOGGLE */}
                    {safeLogistics.roadshowSchedule ? (
                        <span className="max-w-[250px] md:max-w-[600px] leading-relaxed">{quote?.title} <span className="mx-1 opacity-50">•</span> <span className="text-brand-neonBlue font-bold">{safeLogistics.roadshowSchedule}</span></span>
                    ) : (
                        <span className="truncate max-w-[200px]">{quote?.title} • {safeEventDate ? new Date(safeEventDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD'}</span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                    <MapPin size={16} className="text-brand-neonBlue shrink-0" />
                    <span className="truncate max-w-[200px]">{safeEventLocation || 'Venue TBD'}</span>
                </div>
                {(safeStartTime || safeSetupTime) && (
                    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium whitespace-nowrap">
                        <Clock size={16} className="text-brand-neonBlue shrink-0" />
                        <span>
                            {safeSetupTime ? `Prep: ${safeSetupTime} ` : ''} 
                            {safeSetupTime && safeStartTime ? '| ' : ''}
                            {safeStartTime ? `Live: ${safeStartTime}${safeEndTime ? ` - ${safeEndTime}` : ''}` : ''}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-2 text-brand-neonPink text-sm font-bold whitespace-nowrap">
                    <User size={16} className="shrink-0" />
                    <span>{safeStaffCount > 0 ? `${safeStaffCount} Quikpix Operator${safeStaffCount !== 1 ? 's' : ''}` : 'Dry Hire (No Staff)'}</span>
                </div>
            </div>
        )}

        <GlassCard className="p-4 md:p-8 border-brand-neonBlue/20 shadow-2xl mb-8 transition-all duration-500">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <div className="w-2 h-6 bg-brand-neonBlue rounded-full"></div> Your Package
                </h3>
            </div>
            
            <div className="overflow-hidden rounded-xl border border-white/10 mb-8 transition-all duration-500">
                <table className="w-full text-left">
                    <thead className="bg-white/5 text-slate-400 text-[10px] uppercase font-bold tracking-widest border-b border-white/10">
                        <tr>
                            <th className="p-5">Description</th>
                            <th className="p-5 text-center">Qty</th>
                            <th className="p-5 text-right">Unit Rate</th>
                            <th className="p-5 text-center">Disc %</th>
                            <th className="p-5 text-right">Net Total</th>
                            <th className="p-5 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-sm">
                        
                        {mandatoryItems.map((item, i) => {
                            const rowGross = (item?.price || 0) * (item?.quantity || 0);
                            const discPercent = Number((item as any).discount) || 0;
                            const rowDiscount = rowGross - linePrice(item);
                            return (
                                <tr key={`man-${i}`} className="hover:bg-white/5 transition-colors text-white">
                                    <td className="p-5">
                                        <div className="text-white font-bold text-base mb-1">{item?.name || 'Item'}</div>
                                        <div className="text-slate-400 text-xs italic leading-relaxed">{item?.description}</div>
                                    </td>
                                    <td className="p-5 text-center font-medium">{item?.quantity || 1}</td>
                                    <td className="p-5 text-right font-medium">${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="p-5 text-center font-bold text-brand-neonPink">{discPercent > 0 ? `${discPercent}%` : '-'}</td>
                                    <td className="p-5 text-right font-bold text-brand-neonBlue">${(rowGross - rowDiscount).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="p-5 text-center"></td>
                                </tr>
                            );
                        })}

                        <AnimatePresence>
                            {optionalItems.filter(i => selectedUpgrades.has(i.id)).map((item) => {
                                const rowGross = (item?.price || 0) * (item?.quantity || 0);
                                const discPercent = Number((item as any).discount) || 0;
                                const rowDiscount = rowGross - linePrice(item);
                                return (
                                    <motion.tr 
                                        key={`sel-${item.id}`}
                                        initial={{ opacity: 0, y: -20, backgroundColor: 'rgba(236, 72, 153, 0.1)' }}
                                        animate={{ opacity: 1, y: 0, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        className="group text-white border-l-4 border-brand-neonPink relative"
                                    >
                                        <td className="p-5 pl-4">
                                            <div className="text-white font-bold text-base mb-1 flex items-center gap-2">
                                                {item?.name} 
                                                <span className="text-[9px] bg-brand-neonPink/20 border border-brand-neonPink/30 text-brand-neonPink px-2 py-0.5 rounded uppercase tracking-widest font-black">
                                                    Added Upgrade
                                                </span>
                                            </div>
                                            <div className="text-slate-400 text-xs italic leading-relaxed">{item?.description}</div>
                                        </td>
                                        <td className="p-5 text-center font-medium">{item?.quantity || 1}</td>
                                        <td className="p-5 text-right font-medium">
                                            {discPercent > 0 && <span className="block text-slate-500 line-through text-[10px]">${rowGross.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                                            ${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="p-5 text-center font-bold text-brand-neonPink">{discPercent > 0 ? `${discPercent}%` : '-'}</td>
                                        <td className="p-5 text-right font-bold text-brand-neonPink">${(rowGross - rowDiscount).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td className="p-5 text-center">
                                            <button 
                                                onClick={() => toggleUpgrade(item.id)}
                                                className="text-slate-500 hover:text-rose-500 hover:bg-rose-500/10 p-2 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                                                title="Remove Upgrade"
                                            >
                                                <X size={16} />
                                            </button>
                                        </td>
                                    </motion.tr>
                                );
                            })}
                        </AnimatePresence>
                    </tbody>
                </table>
            </div>

            {/* Subtotal & Grand Total */}
            {isTemplate ? (
                <div className="flex justify-end pt-8 mt-8 border-t border-white/10">
                    <div className="text-right">
                        <span className="text-brand-neonBlue uppercase tracking-widest font-bold text-sm">Base Package Estimate</span>
                        <div className="type-numeric text-3xl text-white">${(totalGrossItems).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-sm text-slate-500">+ GST</span></div>
                        <p className="text-slate-500 text-xs mt-2">* Logistics, travel, and custom add-ons quoted per event.</p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="text-slate-400 text-sm max-w-sm">
                        <p>* A 50% deposit is required to secure your booking date.</p>
                        <p>* All prices are in NZD and exclude GST unless specified.</p>
                    </div>
                    <div className="w-full md:w-64 space-y-2">
                        <div className="flex justify-between text-slate-400">
                            <span>Gross Subtotal</span>
                            <span>${(totalGrossItems + logisticsCost).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        {totalVIPDiscount > 0 && (
                            <div className="flex justify-between text-brand-neonPink font-bold">
                                <span className="flex items-center gap-1"><Tag size={12}/> VIP Discount</span>
                                <span>-${totalVIPDiscount.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-slate-400 border-t border-white/5 pt-2">
                            <span>Subtotal (Excl. GST)</span>
                            <span>${subtotal.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                            <span>GST (15%)</span>
                            <span>${gst.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="type-numeric flex justify-between text-2xl text-white pt-4 border-t border-white/10 mt-2 transition-all">
                            <span>Total</span>
                            <span className="text-brand-neonBlue">${total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                </div>
            )}
        </GlassCard>

        {/* ⚡ THE NEW CORE EXPERIENCE SECTION ⚡ */}
        {optionalItems.filter(i => !selectedUpgrades.has(i.id) && (i as any).isCoreChoice).length > 0 && (
            <div className="mb-12 relative z-10">
                <h3 className="type-heading text-2xl text-white mb-2 flex items-center gap-2">
                    <div className="w-2 h-6 bg-brand-neonBlue rounded-full"></div> Choose Your Experience
                </h3>
                <p className="text-slate-400 text-sm mb-6">Select your primary package or booth style below.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <AnimatePresence>
                        {optionalItems.filter(i => !selectedUpgrades.has(i.id) && (i as any).isCoreChoice).map((item) => {
                            const gross = (item?.price || 0) * (item?.quantity || 0);
                            const discPercent = Number((item as any).discount) || 0;
                            const savings = gross - linePrice(item);
                            const net = gross - savings;

                            return (
                                <motion.div 
                                    key={`core-${item.id}`} 
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    onClick={() => toggleUpgrade(item.id)}
                                    className="flex flex-col justify-between p-8 rounded-[32px] border-2 border-brand-neonBlue/30 bg-brand-neonBlue/5 hover:bg-brand-neonBlue/10 hover:border-brand-neonBlue transition-all duration-300 cursor-pointer group shadow-[0_0_30px_rgba(34,211,238,0.1)] hover:shadow-[0_0_50px_rgba(34,211,238,0.2)]"
                                >
                                    <div className="mb-8">
                                        <div className="flex justify-between items-start mb-4">
                                            <h4 className="type-heading text-white text-2xl group-hover:text-brand-neonBlue transition-colors pr-4">{item.name}</h4>
                                            {discPercent > 0 && (
                                                <span className="text-[10px] bg-brand-neonBlue/20 text-brand-neonBlue px-3 py-1 rounded-full uppercase font-black tracking-widest border border-brand-neonBlue/30 shrink-0">
                                                    {discPercent}% VIP Off
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                                    </div>
                                    
                                    <div className="flex justify-between items-end pt-6 border-t border-brand-neonBlue/20">
                                        <div>
                                            {discPercent > 0 && (
                                                <div className="type-numeric text-slate-500 line-through text-xs mb-1">
                                                    ${gross.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                            )}
                                            <div className="type-numeric text-brand-neonBlue text-3xl tracking-tight transition-colors">
                                                + ${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                            <div className="text-slate-500 text-[10px] uppercase tracking-widest mt-1.5 font-bold">
                                                Qty: {item?.quantity || 1}
                                            </div>
                                        </div>
                                        <div className="w-14 h-14 rounded-full bg-brand-neonBlue text-slate-950 flex items-center justify-center group-hover:scale-110 transition-transform shadow-neon">
                                            <Plus size={28} strokeWidth={3} />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            </div>
        )}

        {/* ⚡ STANDARD UPGRADES SECTION ⚡ */}
        {optionalItems.filter(i => !selectedUpgrades.has(i.id) && !(i as any).isCoreChoice).length > 0 && (
            <GlassCard className="p-8 border-brand-neonPink/20 bg-brand-neonPink/5 relative overflow-hidden transition-all duration-500">
                <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                    <PlusCircle size={100} className="text-brand-neonPink"/>
                </div>
                <div className="relative z-10">
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                        <div className="w-2 h-6 bg-brand-neonPink rounded-full"></div> Available Upgrades & Add-ons
                    </h3>
                    <p className="text-slate-400 text-sm mb-6">Click any of the options below to add them to your final package.</p>
                    
                    <div className="space-y-3">
                        <AnimatePresence>
                            {optionalItems.filter(i => !selectedUpgrades.has(i.id) && !(i as any).isCoreChoice).map((item, i) => {
                                const gross = (item?.price || 0) * (item?.quantity || 0);
                                const discPercent = Number((item as any).discount) || 0;
                                const savings = gross - linePrice(item);
                                const net = gross - savings;

                                return (
                                    <motion.div 
                                        key={`avail-${item.id}`} 
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        onClick={() => toggleUpgrade(item.id)}
                                        className="flex justify-between items-center p-6 rounded-2xl border-2 border-white/5 bg-slate-950/40 hover:border-brand-neonPink/50 transition-all duration-300 cursor-pointer group shadow-sm"
                                    >
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <h4 className="text-white font-bold group-hover:text-brand-neonPink transition-colors">{item.name}</h4>
                                                {discPercent > 0 && (
                                                    <span className="text-[10px] bg-brand-neonPink/20 text-brand-neonPink px-2 py-0.5 rounded uppercase font-bold tracking-wider border border-brand-neonPink/30">
                                                        {discPercent}% VIP Off
                                                    </span>
                                                )}
                                            </div>
                                            {item.description && <p className="text-slate-400 text-xs mt-1 leading-relaxed">{item.description}</p>}
                                        </div>
                                        
                                        <div className="text-right pl-4 border-l border-white/10 flex items-center gap-4">
                                            <div>
                                                {discPercent > 0 && (
                                                    <div className="type-numeric text-slate-500 line-through text-xs mb-0.5">
                                                        ${gross.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </div>
                                                )}
                                                <div className="text-brand-neonPink font-bold text-lg transition-colors whitespace-nowrap">
                                                    + ${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                                <div className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5 font-bold">
                                                    Qty: {item?.quantity || 1}
                                                </div>
                                            </div>
                                            <div className="w-10 h-10 rounded-full border-2 border-slate-700 flex items-center justify-center transition-all group-hover:border-brand-neonPink group-hover:text-brand-neonPink">
                                                <PlusCircle size={20} />
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                </div>
            </GlassCard>
        )}
      </div>
    </div>
  );

  const AcceptSlide = () => (
    <div className="h-full flex flex-col items-center justify-center bg-slate-900 p-4 text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-brand-neonBlue to-brand-neonPink rounded-full blur-[150px]"></div>
      </div>
      
      <div className="relative z-10 max-w-2xl w-full">
        <h2 className="type-display text-4xl md:text-5xl text-white mb-6">Ready to create magic?</h2>
        <p className="text-xl text-slate-300 mb-12">
          Approve this proposal to lock in your date and secure the hardware.
        </p>

        {isAcceptedQuote(quote) ? (
          <div className="p-12 text-center bg-slate-800/50 rounded-2xl border border-green-500/30">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(34,197,94,0.4)]">
              <CheckCircle size={32} className="text-slate-900" />
          </div>
          <h3 className="type-display text-3xl text-white mb-4">Quote Accepted!</h3>
          <p className="text-slate-400 mb-8">Thank you for securing your date! To ensure a flawless experience, we need a few technical details from you.</p>
          
          <a 
              href={clientRoute('portal',quote.id)}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-brand-neonBlue text-slate-950 font-bold px-10 py-5 rounded-2xl shadow-neon hover:scale-105 transition-transform"
          >
              Next Step: Complete Event Blueprint →
          </a>
      </div>
        ) : isSupersededQuote(quote) ? (
          <p className="p-8 text-slate-300">This proposal has been superseded and can no longer be approved. Please contact Quikpix for the current proposal.</p>
        ) : quote?.status === 'REJECTED' ? (
          <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-8">
            <X size={48} className="text-red-500 mx-auto mb-4"/>
            <h3 className="text-2xl font-bold text-white mb-2">Quote Declined</h3>
            <p className="text-red-300">If you change your mind, please let us know.</p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" onClick={handleApprove} disabled={calculationReviewRequired || expired || isApproving} className="text-lg px-12 py-6 shadow-[0_0_30px_rgba(34,211,238,0.4)] hover:shadow-[0_0_50px_rgba(34,211,238,0.6)]">
              <Check size={24} className="mr-2"/> Approve & Sign
            </Button>
            <Button variant="outline" size="lg" onClick={handleReject} className="text-lg px-8 py-6 border-red-500/30 text-red-400 hover:bg-red-500/10">
              <X size={24} className="mr-2"/> Decline
            </Button>
          </div>
        )}
        
        <div className="mt-12 text-sm text-slate-500">
          <p>By approving, you agree to QuikPix Terms of Trade.</p>
          <p className="mt-2">Questions? Call us on <span className="text-white">0800 QUIKPIX</span></p>
        </div>
      </div>
    </div>
  );

  const CustomSlide = ({ data }: { data: any }) => (
    <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center overflow-hidden">
        <img 
            src={data.url || data.imageUrl || data.fileUrl || data.file_url} 
            alt="Custom Presentation Slide" 
            className="w-full h-full object-contain" 
        />
    </div>
  );

  const PortfolioCTASlide = () => (
    <div className="h-full flex flex-col items-center justify-center bg-slate-900 p-4 text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-brand-neonBlue to-brand-neonPink rounded-full blur-[150px]"></div>
      </div>
      <div className="relative z-10 max-w-2xl w-full animate-fade-in-up">
        <h2 className="type-display text-4xl md:text-5xl text-white mb-6">Ready to elevate your next event?</h2>
        <p className="text-xl text-slate-300 mb-12">
          This is a general pricing and services guide. Contact our team to check availability and build a custom package for your specific date.
        </p>
        <a href="mailto:info@quikpix.co.nz" className="inline-block bg-brand-neonBlue text-slate-950 font-bold px-10 py-5 rounded-2xl shadow-neon hover:scale-105 transition-transform">
          Contact to Book
        </a>
      </div>
    </div>
  );

  return (
    <div className={`quote-preview-shell min-h-screen ${viewMode === 'PRESENTATION' ? 'bg-slate-950 fixed inset-0 z-[100] overflow-hidden' : 'bg-slate-50 pb-12 relative'}`}>
      <div ref={previewHeaderRef} data-testid="quote-preview-header" className={viewMode === 'PRESENTATION' ? 'fixed top-0 inset-x-0 z-[120] flex flex-col' : 'relative'}>
      {isSupersededQuote(quote) && <p className="relative z-[110] bg-slate-100 p-3 text-slate-800">This proposal has been superseded and can no longer be approved. Please contact Quikpix for the current proposal.</p>}
      {(expired || canRenewValidity) && <div role={expired ? 'alert' : undefined} className="relative z-[110] bg-slate-100 p-3 text-slate-800 print:hidden">
        <p>{expired ? `This quote expired on ${displayValidityDate(validUntil)}. ` : `Valid until ${displayValidityDate(validUntil)}. `}
          {expired && (isPublicView ? 'Please contact Quikpix for an updated validity date.' : 'Renew its validity before client approval.')}</p>
        {canRenewValidity && <button className="underline font-semibold mt-2" onClick={() => { setNewValidUntil(nzDateAfterDays(14)); setValidityError(''); setValidityOpen(true); }}>Renew Validity</button>}
      </div>}
      {validityOpen && <div role="dialog" aria-modal="true" aria-label="Renew quote validity" className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-950/90 p-4">
        <form onSubmit={saveValidity} className="w-full max-w-lg rounded-xl border border-white/20 bg-slate-900 p-6 text-white">
          <h2 className="text-xl font-semibold mb-4">Renew Validity</h2>
          <p className="mb-4">Only the valid-until date will change. Prices, staffing and the existing calculation review are preserved.</p>
          <label>Valid until<input type="date" required min={nzDateAfterDays()} value={newValidUntil} onChange={event => setNewValidUntil(event.target.value)} className="block w-full rounded border border-white/30 bg-slate-950 p-3 mt-2" /></label>
          {validityError && <p role="alert" className="my-4 text-amber-200">{validityError}</p>}
          <div className="flex gap-3 mt-4"><Button type="submit" disabled={renewingValidity}>{renewingValidity ? 'Saving…' : 'Confirm New Validity'}</Button>
            <Button type="button" variant="outline" disabled={renewingValidity} onClick={() => setValidityOpen(false)}>Cancel</Button></div>
        </form>
      </div>}
      {calculationReviewRequired && <div role="alert" className="relative z-[110] bg-amber-100 p-3 text-amber-950">
        <p>Calculation review required before approval. Check the saved staffing policy and percentage discounts.</p>
        <button className="underline font-semibold mt-2" onClick={reviewCalculation}>Review Calculation</button>
      </div>}
      {isPublicView && signatureOpen && <div role="dialog" aria-modal="true" aria-label="Approve and sign quote" className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-950/90 p-4">
        <form className="w-full max-w-lg rounded-xl border border-white/20 bg-slate-900 p-6 text-white" onSubmit={event => { event.preventDefault(); void handleApprove(); }}>
          <h2 className="text-xl font-semibold mb-4">Approve &amp; Sign</h2>
          <label className="block">Your full name<input autoFocus required minLength={2} maxLength={200} value={signerName} onChange={event => setSignerName(event.target.value)} className="block w-full rounded border border-white/30 bg-slate-950 p-3 mt-2" /></label>
          <label className="flex gap-3 my-4"><input required type="checkbox" checked={termsAgreed} onChange={event => setTermsAgreed(event.target.checked)} />I agree to the quote and Quikpix Terms of Trade. My typed name is my signature.</label>
          {approvalError && <p role="alert" className="my-4 text-amber-200">{approvalError}</p>}
          <div className="flex gap-3"><Button type="submit" disabled={isApproving || !termsAgreed || signerName.trim().length < 2}>{isApproving ? 'Approving…' : 'Confirm Approval'}</Button>
            <Button type="button" variant="outline" disabled={isApproving} onClick={() => setSignatureOpen(false)}>Cancel</Button></div>
        </form>
      </div>}
      {pricing.preserved && <p className="relative z-[110] bg-slate-100 p-3 text-slate-700">Accepted commercial totals are preserved. Line calculations may differ on legacy Quotes; changes require an explicitly reviewed draft revision.</p>}
      
      <AnimatePresence>
          {showMobileNotice && (
              <motion.div 
                  initial={{ opacity: 0, y: -50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  className="fixed top-4 left-4 right-4 z-[100] bg-slate-900 border border-brand-neonBlue/30 shadow-2xl rounded-2xl p-4 flex gap-4 items-start md:hidden"
              >
                  <div className="bg-brand-neonBlue/20 text-brand-neonBlue p-2 rounded-lg shrink-0">
                      <Monitor size={20} />
                  </div>
                  <div className="flex-1">
                      <h4 className="text-white font-bold text-sm mb-1">Document View</h4>
                      <p className="text-slate-400 text-xs leading-relaxed">
                          Presentation View is designed for a larger screen. You can view the full quote below.
                      </p>
                      <button
                          type="button"
                          onClick={() => {
                              setViewMode('DOCUMENT');
                              setShowMobileNotice(false);
                          }}
                          className="mt-3 min-h-11 rounded-lg bg-brand-neonBlue px-4 text-xs font-bold text-slate-950"
                      >
                          View Quote
                      </button>
                  </div>
                  <button aria-label="Dismiss presentation information" onClick={() => setShowMobileNotice(false)} className="min-h-11 min-w-11 text-slate-500 hover:text-white flex items-center justify-center">
                      <X size={16} />
                  </button>
              </motion.div>
          )}
      </AnimatePresence>

      <div data-testid="quote-preview-toolbar" className={`relative z-50 flex flex-wrap sm:flex-nowrap justify-between items-center p-3 md:p-4 gap-2 md:gap-4 transition-all duration-300 overflow-x-clip ${viewMode === 'PRESENTATION' ? 'order-first shrink-0 h-[76px] bg-slate-950/95 hover:opacity-100 opacity-70 print:hidden' : 'bg-white border-b border-slate-200 shadow-sm opacity-100 print:hidden'}`}>
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1 basis-[9rem]">
              {!isPublicView && (
                  <Button variant="outline" size="sm" onClick={onBack} className={`shrink-0 ${viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : ''}`}>
                      <ArrowLeft size={16} className="md:mr-2"/> <span className="hidden md:inline">Back</span>
                  </Button>
              )}
              <div className={`min-w-0 ${viewMode === 'PRESENTATION' ? 'text-white' : 'text-slate-900'}`}>
                  <div className="font-bold text-xs md:text-lg truncate max-w-[150px] md:max-w-md" title={quote?.title}>{quote?.title}</div>
                  <div className="text-[9px] md:text-xs opacity-60 hidden sm:block">#Q-{quoteRef}</div>
              </div>
          </div>

          <div className="flex items-center justify-end gap-1.5 md:gap-2 min-w-0 max-w-full shrink-0">
              {!isPublicView && (
                  <>
                  {onDuplicateQuote && quote?.status !== 'DRAFT' && (
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => onDuplicateQuote(quote)} 
                            className={`hidden md:flex ${viewMode === 'PRESENTATION' 
                                ? 'bg-brand-neonPink/20 text-brand-neonPink border-brand-neonPink/50 hover:bg-brand-neonPink hover:text-slate-950' 
                                : 'text-brand-neonPink border-brand-neonPink/30 hover:bg-brand-neonPink/10'}`}
                        >
                            <FilePlus size={16} className="mr-2"/> Revise Proposal
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={onEdit} className={viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : 'text-slate-600'}>
                        <Edit3 size={16} className="md:mr-2"/> <span className="hidden md:inline">Edit</span>
                    </Button>
                    
                    {/* ⚡ NEW SILENT APPROVE BUTTON */}
                    {quote?.status !== 'APPROVED' && (quote?.status as string) !== 'COMPLETED' && (quote?.status as string) !== 'WITHDRAWN' && !isSupersededQuote(quote) && (
                        <Button 
                            variant="outline"
                            size="sm" 
                            disabled={calculationReviewRequired}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('SILENT APPROVE: Approve this quote and generate event blueprints WITHOUT sending to the client?')) {
                                    handleApprove();
                                }
                            }} 
                            className={`hidden md:flex transition-all ${viewMode === 'PRESENTATION' 
                                ? 'bg-brand-neonBlue/20 text-brand-neonBlue border-brand-neonBlue/30 hover:bg-brand-neonBlue hover:text-slate-950' 
                                : 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/30 hover:bg-brand-neonBlue/20 shadow-sm'}`}
                        >
                            <CheckCircle size={16} className="md:mr-2"/> <span className="hidden lg:inline">Silent Approve</span>
                        </Button>
                    )}

                    <div className="relative group hidden sm:block">
                        <Button variant="outline" size="sm" className={viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : 'text-slate-600'}>
                            Status <ChevronDown size={14} className="ml-1 opacity-70"/>
                        </Button>
                        <div className="absolute right-0 top-full mt-1 w-40 bg-slate-900 border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1">
                            {canSupersedeQuote(quote) && <button onClick={() => onStatusChange?.('SUPERSEDED')} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-400 hover:bg-white/5 transition-colors">Mark Superseded</button>}
                            <button 
                                onClick={() => onStatusChange && onStatusChange('DRAFT')}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-yellow-400 hover:bg-white/5 transition-colors"
                            >
                                Revert to Draft
                            </button>
                            <button 
                                onClick={() => onStatusChange && onStatusChange('REJECTED')}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-rose-400 hover:bg-white/5 transition-colors"
                            >
                                Mark Rejected
                            </button>
                            <button 
                                onClick={() => onStatusChange && onStatusChange('WITHDRAWN' as any)}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-slate-400 hover:bg-white/5 transition-colors"
                            >
                                Withdraw / Archive
                            </button>
                        </div>
                    </div>

                    <Button variant="primary" size="sm" onClick={handleSend} className={`hidden sm:flex ${viewMode === 'PRESENTATION' ? 'bg-brand-neonBlue text-black' : ''}`} disabled={isSending || quote?.status === 'APPROVED' || isSupersededQuote(quote)}>
                        <Send size={16} className="mr-2"/> {isSending ? 'Sending...' : 'Send'}
                    </Button>
                  </>
              )}

              {isPublicView && !isSupersededQuote(quote) && viewMode === 'PRESENTATION' && currentStep !== proposalSlides.length - 1 && (
                  <Button 
                      size="sm" 
                      onClick={() => setCurrentStep(proposalSlides.length - 1)} 
                      className="bg-brand-neonBlue text-slate-950 font-black uppercase tracking-widest text-[9px] shadow-neon mr-1 md:mr-2 border-none px-2 md:px-3"
                  >
                      <span className="hidden md:inline">Skip to </span>Approve
                  </Button>
              )}
              
              {!isMobileDevice && (
                  <Button variant="outline" size="sm" onClick={() => setViewMode(viewMode === 'PRESENTATION' ? 'DOCUMENT' : 'PRESENTATION')} aria-label={viewMode === 'PRESENTATION' ? 'Switch to document view' : 'Switch to presentation view'} className={`px-2 md:px-3 ${viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : ''}`}>
                      {viewMode === 'PRESENTATION' ? <><Minimize2 size={16} className="md:mr-2"/> <span className="hidden md:inline">Document View</span></> : <><Maximize2 size={16} className="md:mr-2"/> <span className="hidden md:inline">Presentation Mode</span></>}
                  </Button>
              )}
              
              <div className="relative group hidden sm:block">
                  <Button variant="outline" size="sm" className={`px-2 md:px-3 ${viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : 'text-slate-600'}`}>
                      <Download size={16} className="md:mr-2"/> <span className="hidden md:inline">Download</span> <ChevronDown size={14} className="ml-1 opacity-70"/>
                  </Button>
                  <div className="absolute right-0 top-full mt-1 w-56 bg-slate-900 border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1 overflow-hidden">
                      <button onClick={handleDownloadDeck} className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-brand-neonBlue/20 hover:text-brand-neonBlue transition-colors border-b border-white/5 flex items-center gap-2">
                          <Layout size={14} /> Presentation Deck
                      </button>
                      <button onClick={handleDownloadQuote} className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-brand-neonBlue/20 hover:text-brand-neonBlue transition-colors flex items-center gap-2">
                          <FileText size={14} /> Formal Quote
                      </button>
                  </div>
              </div>
              
              {!isPublicView && (
                  <Button variant="outline" size="sm" onClick={handleShare} className={`px-2 md:px-3 ${viewMode === 'PRESENTATION' ? 'bg-white/10 text-white border-white/20' : ''}`}>
                      <Share2 size={16} className="md:mr-2"/> <span className="hidden md:inline">{isSharing ? 'Copied!' : 'Link'}</span>
                  </Button>
              )}
          </div>
      </div>

      </div>

      {viewMode === 'PRESENTATION' && (
          <div className="fixed inset-0 overflow-hidden bg-slate-950" data-testid="presentation-viewport">
            <div
              data-testid="presentation-stage-area"
              className="absolute inset-x-0 overflow-hidden bg-slate-950"
              style={{ top: presentationHeaderHeight, bottom: PRESENTATION_CONTROLS_HEIGHT }}
            >
            <div
              data-testid="presentation-stage"
              data-presentation-width={PRESENTATION_CANVAS_WIDTH}
              data-presentation-height={PRESENTATION_CANVAS_HEIGHT}
              data-presentation-scale={presentationScale}
              className="absolute left-1/2 top-1/2 h-[1080px] w-[1920px] origin-center overflow-hidden"
              style={{ transform: `translate(-50%, -50%) scale(${presentationScale})` }}
            >
              <AnimatePresence mode="wait">
                  <motion.div key={currentStep} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="h-full w-full">
                      {proposalSlides[currentStep].type === 'COVER' && <CoverSlide />}
                      {proposalSlides[currentStep].type === 'INTRO' && <IntroSlide text={proposalSlides[currentStep].data.text} />}
                      {proposalSlides[currentStep].type === 'PRODUCT' && <ProductSlide data={proposalSlides[currentStep].data} />}
                      {proposalSlides[currentStep].type === 'PRICING' && <PricingSlide />}
                      {proposalSlides[currentStep].type === 'ACCEPT' && <AcceptSlide />}
                      {proposalSlides[currentStep].type === 'CUSTOM' && <CustomSlide data={proposalSlides[currentStep].data} />}
                      {proposalSlides[currentStep].type === 'PORTFOLIO_CTA' && <PortfolioCTASlide />}
                  </motion.div>
              </AnimatePresence>
            </div>
            </div>

            {proposalSlides[currentStep].type !== 'COVER' && (
              <div
                data-testid="presentation-navigation"
                className="absolute inset-x-0 bottom-0 z-50 h-20 flex items-center justify-center bg-gradient-to-t from-slate-950 via-slate-950 to-slate-950/80"
              >
                <div className="flex items-center gap-6">
                  <button onClick={handlePrev} disabled={currentStep === 0} aria-label="Previous proposal slide" className="p-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-brand-neonBlue hover:text-black disabled:opacity-30 transition-all">
                      <ChevronLeft size={24}/>
                  </button>
                  <div className="flex gap-2">
                      {proposalSlides.map((_, idx) => (
                          <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentStep ? 'w-8 bg-brand-neonBlue' : 'w-2 bg-white/30'}`} />
                      ))}
                  </div>
                  <button 
                      onClick={handleNext} 
                      disabled={currentStep === proposalSlides.length - 1} 
                      aria-label="Next proposal slide"
                      className={`flex items-center justify-center rounded-full backdrop-blur-md text-white hover:bg-brand-neonBlue hover:text-slate-950 disabled:opacity-30 transition-all duration-300 ${
                          currentStep === proposalSlides.length - 2 
                              ? 'pl-6 pr-4 py-3 bg-brand-neonBlue/20 border border-brand-neonBlue/50 text-brand-neonBlue hover:shadow-neon' 
                              : 'p-4 bg-white/10'
                      }`}
                  >
                      {currentStep === proposalSlides.length - 2 && (
                          <span className="text-[10px] font-black uppercase tracking-widest mr-2">Review & Approve</span>
                      )}
                      <ChevronRight size={24}/>
                  </button>
                </div>
              </div>
            )}
          </div>
      )}

      {viewMode === 'DOCUMENT' && (
          <>
          <div className="md:hidden flex items-center justify-between bg-slate-900 px-4 py-3 sticky top-[56px] z-40 border-b border-white/10 shadow-lg mt-14 print:hidden">
             <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Mobile Web View</span>
             <button onClick={handleDownloadQuote} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-brand-neonBlue bg-brand-neonBlue/10 px-3 py-1.5 rounded-lg border border-brand-neonBlue/30 shadow-neon-blue-sm">
                <Download size={12} /> Download PDF Quote
             </button>
          </div>

          <div id="quote-print-container" data-testid="quote-document" className="quote-document font-sans w-full md:max-w-[210mm] mx-auto bg-white min-h-[297mm] shadow-xl my-4 md:my-8 p-6 sm:p-8 md:p-[15mm] text-slate-900 overflow-hidden print:shadow-none print:m-0 print:w-full print:p-0">
              
              {/* ⚡ THE PDF CSS ENGINE ⚡ */}
              <style media="print">
                  {`
                    @page { 
                        size: A4 portrait; 
                        margin: 10mm; 
                    }
                    html, body, #root {
                        background: white !important;
                    }
                    body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    body * { visibility: hidden; }
                    #quote-print-container, #quote-print-container * { visibility: visible; }
                    #quote-print-container { 
                        position: static;
                        width: 100%; 
                    }
                    .print-avoid-break {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .print-hide {
                        display: none !important;
                    }
                  `}
              </style>
              
              <div className="flex flex-col md:flex-row justify-between items-start mb-10 border-b border-slate-200 pb-8 mt-4 md:mt-0 print:mt-0 print:border-b-2">
                  <div>
                      <h2 className="type-display text-3xl md:text-4xl text-slate-900 mb-4">Quote</h2>
                      <div className="text-sm text-slate-500 space-y-1 font-medium">
                          <p>Reference: <span className="font-bold text-slate-800">#{quoteRef}</span></p>
                          <p>Date: {safeCreatedAt ? new Date(safeCreatedAt).toLocaleDateString('en-NZ') : 'TBD'}</p>
                          <p>Valid Until: {displayValidityDate(validUntil)}</p>
                          <p>Status: <span className={`font-bold ${quote.status === 'APPROVED' ? 'text-green-500' : 'text-brand-neonBlue'}`}>{quote.status || 'DRAFT'}</span></p>
                      </div>
                  </div>
                  <div className="text-left md:text-right mt-8 md:mt-0">
                      <h3 className="type-display text-xl text-brand-neonBlue mb-2">QUIKPIX</h3>
                      <div className="text-sm text-slate-500 space-y-1 font-medium">
                          <p>Auckland, New Zealand</p>
                          <p>0274 932 338</p>
                          <p>info@quikpix.co.nz</p>
                      </div>
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16 print-avoid-break">
                  <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 md:mb-4">Prepared For</h3>
                      <div className="text-slate-800">
                          <p className="text-xl font-bold mb-1">{safeFirstName} {safeLastName}</p>
                          {displayCompany !== 'Private Account' && <p className="text-brand-neonBlue font-bold mb-2 break-words">{displayCompany}</p>}
                          <div className="text-sm text-slate-500 space-y-1 mt-3">
                              {safeEmail && <p className="flex items-start gap-2 min-w-0"><Mail size={14} className="shrink-0 mt-0.5"/> <span className="min-w-0 break-all">{safeEmail}</span></p>}
                              {safePhone && <p className="flex items-center gap-2"><Phone size={14} className="shrink-0"/> {safePhone}</p>}
                          </div>
                      </div>
                  </div>
                  
                  <div className="min-w-0">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 md:mb-4">Event Details</h3>
                      <div className="text-slate-800 text-sm space-y-4">
                          <div><span className="block text-xs text-slate-500 mb-1">Event Name</span><span className="font-bold text-lg leading-tight block">{quote?.title}</span></div>
                          <div className="grid grid-cols-2 gap-4 md:gap-6">
                              <div>
                                  <span className="block text-xs text-slate-500 mb-1">Date</span>
                                  {/* ⚡ SMART DATE TOGGLE */}
                                  {safeLogistics.roadshowSchedule ? (
                                      <span className="font-bold text-brand-neonBlue">{safeLogistics.roadshowSchedule}</span>
                                  ) : (
                                      <span className="font-bold">{safeEventDate ? new Date(safeEventDate).toLocaleDateString() : 'TBD'}</span>
                                  )}
                              </div>
                              <div className="min-w-0"><span className="block text-xs text-slate-500 mb-1">Location</span><span className="font-bold block break-words">{safeEventLocation || 'Venue TBD'}</span></div>
                              
                              {/* ⚡ NEW: CREW ALLOCATION IN DOCUMENT VIEW */}
                              {!isTemplate && (
                                  <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                                      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Crew Allocation</span>
                                      <span className={`font-bold ${safeStaffCount > 0 ? 'text-brand-neonPink' : 'text-slate-600'}`}>
                                          {safeStaffCount > 0 ? `${safeStaffCount} Quikpix Operator${safeStaffCount !== 1 ? 's' : ''}` : 'Dry Hire (No Staff Provided)'}
                                      </span>
                                  </div>
                              )}

                              {!isTemplate && (safeSetupTime || safeStartTime) && (
                                  <div className="col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200 mt-2">
                                      {safeSetupTime && <div><span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Crew Arrival</span><span className="font-bold text-brand-neonBlue">{safeSetupTime}</span></div>}
                                      {safeStartTime && <div className="col-span-2"><span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Live Event Hours</span><span className="font-bold text-slate-800">{safeStartTime} - {safeEndTime || 'TBD'}</span></div>}
                                  </div>
                              )}
                          </div>
                      </div>
                  </div>
              </div>

              <div className="mb-12 pb-12 border-b border-slate-100 print-avoid-break">
                  <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                  <div className="md:w-2/3">
                      <div className="flex justify-between items-center mb-3 md:mb-4 print:mb-4">
                          <h3 className="text-xs font-bold text-brand-neonBlue uppercase tracking-widest">Executive Vision</h3>
                      </div>
                      <p className="text-slate-700 leading-relaxed italic whitespace-pre-wrap text-sm">
                          {((quote as any)?.presentation_slide_content?.text || quote?.presentationSlideContent) || "A bespoke experiential vision has been crafted for this event."}
                      </p>
                  </div>
                      <div className="md:w-1/3 bg-slate-50 p-6 rounded-xl border border-slate-100">
                          <h4 className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-tight">The Quikpix Edge</h4>
                          <ul className="space-y-3 text-[10px] text-slate-500">
                              <li className="flex gap-2"><Check size={12} className="text-brand-neonBlue shrink-0"/> 20+ years of premium service.</li>
                              <li className="flex gap-2"><Check size={12} className="text-brand-neonBlue shrink-0"/> Proprietary software & hardware.</li>
                              <li className="flex gap-2"><Check size={12} className="text-brand-neonBlue shrink-0"/> 24/7 expert technical support.</li>
                          </ul>
                      </div>
                  </div>
              </div>

              <div className="mb-12">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Your Investment</h3>
                  
                  {/* MOBILE VIEW IS HIDDEN ON PRINT */}
                  <div className="md:hidden space-y-6 print-hide">
                      {mandatoryItems.map((item, i) => {
                          const gross = (item?.price || 0) * (item?.quantity || 0);
                          const discPercent = Number((item as any).discount) || 0;
                          const savings = gross - linePrice(item);
                          const net = gross - savings;
                          return (
                              <div key={`mob-man-${i}`} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                                  <div className="p-5">
                                      <h4 className="font-bold text-lg text-slate-900 mb-2">{item?.name || 'Item'}</h4>
                                      <p className="text-slate-500 text-sm leading-relaxed">{item?.description}</p>
                                  </div>
                                  <div className="bg-slate-50 p-5 border-t border-slate-100 flex flex-col gap-3">
                                      <div className="flex justify-between items-center text-sm">
                                          <span className="text-slate-500 font-medium">Quantity</span>
                                          <span className="font-bold text-slate-900">{item?.quantity || 1}</span>
                                      </div>
                                      <div className="flex justify-between items-center text-sm">
                                          <span className="text-slate-500 font-medium">Unit Rate</span>
                                          <span className="font-bold text-slate-900">${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                      {discPercent > 0 && (
                                          <div className="flex justify-between items-center text-sm text-brand-neonPink">
                                              <span className="font-medium">VIP Discount</span>
                                              <span className="font-bold">{discPercent}% OFF</span>
                                          </div>
                                      )}
                                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-200">
                                          <span className="text-xs uppercase tracking-widest font-bold text-slate-400">Line Total</span>
                                          <span className="font-black text-xl text-brand-neonBlue">${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                  </div>
                              </div>
                          );
                      })}

                      {optionalItems.filter(i => selectedUpgrades.has(i.id)).map((item, i) => {
                          const gross = (item?.price || 0) * (item?.quantity || 0);
                          const discPercent = Number((item as any).discount) || 0;
                          const savings = gross - linePrice(item);
                          const net = gross - savings;
                          return (
                              <div key={`mob-opt-${i}`} className="bg-brand-neonPink/5 border border-brand-neonPink/30 rounded-2xl shadow-sm overflow-hidden flex flex-col relative group">
                                  <div className="p-5">
                                      <div className="flex justify-between items-start mb-2">
                                          <div className="flex flex-wrap items-center gap-2">
                                              <h4 className="font-bold text-lg text-slate-900">{item?.name}</h4>
                                              <span className="text-[10px] font-black bg-brand-neonPink text-white px-2 py-1 rounded uppercase tracking-widest">Added Upgrade</span>
                                          </div>
                                          <button 
                                              onClick={() => toggleUpgrade(item.id)} 
                                              className="text-slate-400 hover:text-red-500 p-1 bg-white rounded-full border border-slate-200 shadow-sm"
                                              title="Remove"
                                          >
                                              <X size={14}/>
                                          </button>
                                      </div>
                                      <p className="text-slate-600 text-sm leading-relaxed">{item?.description}</p>
                                  </div>
                                  <div className="bg-white p-5 border-t border-brand-neonPink/20 flex flex-col gap-3">
                                      <div className="flex justify-between items-center text-sm">
                                          <span className="text-slate-500 font-medium">Quantity</span>
                                          <span className="font-bold text-slate-900">{item?.quantity || 1}</span>
                                      </div>
                                      <div className="flex justify-between items-center text-sm">
                                          <span className="text-slate-500 font-medium">Unit Rate</span>
                                          <span className="font-bold text-slate-900">${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                      {discPercent > 0 && (
                                          <div className="flex justify-between items-center text-sm text-brand-neonPink">
                                              <span className="font-medium">VIP Discount</span>
                                              <span className="font-bold">{discPercent}% OFF</span>
                                          </div>
                                      )}
                                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-200">
                                          <span className="text-xs uppercase tracking-widest font-bold text-slate-400">Line Total</span>
                                          <span className="font-black text-xl text-brand-neonPink">${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                  </div>
                              </div>
                          );
                      })}
                  </div>

                  {/* 💻 DESKTOP VIEW (FORCED VISIBLE ON PRINT) */}
                  <div className="hidden md:block w-full min-w-0 text-left border-collapse print:block">
                      <table className="quote-pricing-table w-full table-fixed">
                          <thead className="border-b-2 border-slate-900 text-slate-900 text-[10px] uppercase tracking-widest">
                              <tr>
                                  <th className="py-4 font-black text-left">Description</th>
                                  <th className="py-4 font-black text-center w-16">Qty</th>
                                  <th className="py-4 font-black text-right w-28">Unit Price</th>
                                  <th className="py-4 font-black text-center w-20">VIP Disc</th>
                                  <th className="py-4 font-black text-right w-32">Line Total</th>
                                  <th className="py-4 font-black w-12 print-hide"></th>
                              </tr>
                          </thead>
                          <tbody>
                              {mandatoryItems.map((item, i) => {
                                  const gross = (item?.price || 0) * (item?.quantity || 0);
                                  const discPercent = Number((item as any).discount) || 0;
                                  const savings = gross - linePrice(item);
                                  return (
                                      <tr key={`desk-man-${i}`} className="border-b border-slate-200 bg-white text-slate-900 print-avoid-break">
                                          <td className="py-6 pr-4 break-words">
                                              <div className="font-bold text-base mb-1">{item?.name || 'Item'}</div>
                                              <div className="text-slate-500 text-xs italic leading-relaxed">{item?.description}</div>
                                          </td>
                                          <td className="type-numeric py-6 text-center">{item?.quantity || 1}</td>
                                          <td className="type-numeric py-6 text-right">${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                          <td className="type-numeric py-6 text-center text-brand-neonPink">{discPercent > 0 ? `${discPercent}%` : '-'}</td>
                                          <td className="type-numeric py-6 text-right text-brand-neonBlue">${(gross - savings).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                          <td className="py-6 text-center print-hide"></td>
                                      </tr>
                                  );
                              })}

                              {optionalItems.filter(i => selectedUpgrades.has(i.id)).map((item, i) => {
                                  const gross = (item?.price || 0) * (item?.quantity || 0);
                                  const discPercent = Number((item as any).discount) || 0;
                                  const savings = gross - linePrice(item);
                                  return (
                                      <tr key={`desk-opt-${i}`} className="border-b border-brand-neonPink/30 bg-brand-neonPink/5 text-slate-900 group print-avoid-break">
                                          <td className="py-6 pr-4 pl-4 border-l-4 border-brand-neonPink break-words">
                                              <div className="font-bold text-base mb-1 flex items-center gap-2">
                                                  {item?.name} <span className="text-[9px] bg-brand-neonPink text-white px-2 py-0.5 rounded uppercase tracking-widest font-black">Added Upgrade</span>
                                              </div>
                                              <div className="text-slate-600 text-xs italic leading-relaxed">{item?.description}</div>
                                          </td>
                                          <td className="type-numeric py-6 text-center">{item?.quantity || 1}</td>
                                          <td className="type-numeric py-6 text-right">${(item?.price || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                          <td className="type-numeric py-6 text-center text-brand-neonPink">{discPercent > 0 ? `${discPercent}%` : '-'}</td>
                                          <td className="type-numeric py-6 text-right text-brand-neonPink">${(gross - savings).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                          <td className="py-6 text-center print-hide">
                                              <button 
                                                  onClick={() => toggleUpgrade(item.id)}
                                                  className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                                                  title="Remove Upgrade"
                                              >
                                                  <X size={16} />
                                              </button>
                                          </td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>
              </div>

              {/* ⚡ FIX: SHOW UNSELECTED UPGRADES ON PRINT AS WELL */}
              {optionalItems.filter(i => !selectedUpgrades.has(i.id) && (i as any).isCoreChoice).length > 0 && (
                  <div className="mb-10 print-avoid-break">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                          Choose Your Experience
                      </h3>
                      <p className="text-sm text-slate-500 mb-6">Select your primary package or booth style below.</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {optionalItems.filter(i => !selectedUpgrades.has(i.id) && (i as any).isCoreChoice).map((item, i) => {
                              const gross = (item?.price || 0) * (item?.quantity || 0);
                              const discPercent = Number((item as any).discount) || 0;
                              const savings = gross - linePrice(item);
                              const net = gross - savings;

                              return (
                                  <div 
                                      key={i} 
                                      onClick={() => toggleUpgrade(item.id)}
                                      className="flex flex-col justify-between p-6 rounded-3xl border-2 border-brand-neonBlue/30 bg-brand-neonBlue/5 hover:bg-brand-neonBlue/10 hover:border-brand-neonBlue transition-all duration-300 cursor-pointer group shadow-sm"
                                  >
                                      <div className="mb-6">
                                          <div className="flex justify-between items-start mb-3">
                                              <h4 className="type-heading text-slate-900 text-xl group-hover:text-brand-neonBlue transition-colors pr-2">{item.name}</h4>
                                              {discPercent > 0 && (
                                                  <span className="text-[9px] bg-brand-neonBlue/10 text-brand-neonBlue px-2 py-1 rounded-full uppercase font-black tracking-widest border border-brand-neonBlue/20 shrink-0">
                                                      {discPercent}% VIP Off
                                                  </span>
                                              )}
                                          </div>
                                          <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                                      </div>
                                      
                                      <div className="flex justify-between items-end pt-4 border-t border-brand-neonBlue/20">
                                          <div>
                                              {discPercent > 0 && (
                                                  <div className="type-numeric text-slate-400 line-through text-xs mb-0.5">
                                                      ${gross.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </div>
                                              )}
                                              <div className="type-numeric text-brand-neonBlue text-2xl tracking-tight transition-colors">
                                                  + ${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </div>
                                              <div className="text-slate-500 text-[10px] uppercase tracking-widest mt-1 font-bold">
                                                  Qty: {item?.quantity || 1}
                                              </div>
                                          </div>
                                          {/* ⚡ FIX: Hide the interactive plus button on print so it looks like a clean document */}
                                          <div className="w-10 h-10 rounded-full bg-brand-neonBlue text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md print-hide">
                                              <Plus size={20} strokeWidth={3} />
                                          </div>
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  </div>
              )}

              {optionalItems.filter(i => !selectedUpgrades.has(i.id) && !(i as any).isCoreChoice).length > 0 && (
                  <div className="mb-16 bg-slate-50 p-5 md:p-6 rounded-xl border border-slate-200 print-avoid-break">
                      <h3 className="text-xs font-bold text-brand-neonPink uppercase tracking-widest mb-2">Available Upgrades</h3>
                      <p className="text-xs text-slate-500 mb-4">Click any option below to add it to your final package.</p>
                      
                      <div className="space-y-3">
                          {optionalItems.filter(i => !selectedUpgrades.has(i.id) && !(i as any).isCoreChoice).map((item, i) => {
                              const gross = (item?.price || 0) * (item?.quantity || 0);
                              const discPercent = Number((item as any).discount) || 0;
                              const savings = gross - linePrice(item);
                              const net = gross - savings;

                              return (
                                  <div 
                                      key={i} 
                                      onClick={() => toggleUpgrade(item.id)}
                                      className="flex justify-between items-center bg-white p-4 rounded-lg border border-slate-200 hover:border-brand-neonPink/50 cursor-pointer transition-all group shadow-sm"
                                  >
                                      <div className="pr-4">
                                          <div className="font-bold text-sm text-slate-900 group-hover:text-brand-neonPink transition-colors flex flex-wrap items-center gap-2">
                                              {item?.name}
                                              {discPercent > 0 && (
                                                  <span className="text-[9px] bg-brand-neonPink/10 text-brand-neonPink px-2 py-0.5 rounded uppercase font-black tracking-widest border border-brand-neonPink/20">
                                                      {discPercent}% VIP Off
                                                  </span>
                                              )}
                                          </div>
                                          <div className="text-xs text-slate-500 mt-1 leading-relaxed">{item?.description}</div>
                                      </div>
                                      <div className="flex items-center gap-3 shrink-0">
                                          <div className="text-right">
                                              {discPercent > 0 && (
                                                  <div className="type-numeric text-slate-400 line-through text-[10px] mb-0.5">
                                                      ${gross.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </div>
                                              )}
                                              <div className="font-bold text-slate-900 group-hover:text-brand-neonPink transition-colors whitespace-nowrap">
                                                  + ${net.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </div>
                                              <div className="text-slate-500 text-[9px] uppercase tracking-widest mt-0.5 font-bold">
                                                  Qty: {item?.quantity || 1}
                                              </div>
                                          </div>
                                          <div className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center group-hover:border-brand-neonPink group-hover:text-brand-neonPink transition-colors shrink-0 print-hide">
                                              <PlusCircle size={16} />
                                          </div>
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  </div>
              )}
              
              <div className="flex justify-end mb-16 print-avoid-break">
                  {/* ⚡ WIDENED CONTAINER (w-96) TO PREVENT SQUISHING */}
                  <div className="w-full md:w-96 space-y-4 text-right ml-auto bg-slate-50 md:bg-transparent p-6 md:p-0 rounded-xl md:rounded-none">
                      <div className="type-numeric flex justify-between text-slate-500 text-[10px] uppercase tracking-widest">
                          <span>Gross Package Value</span>
                          <span>${(totalGrossItems + logisticsCost).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      
                      {totalVIPDiscount > 0 && (
                          <div className="type-numeric flex justify-between items-center text-brand-neonPink text-[11px] uppercase tracking-widest bg-brand-neonPink/5 p-3 rounded-xl border border-brand-neonPink/20">
                              <span className="flex items-center gap-1"><Tag size={12}/> VIP Savings</span>
                              <span>-${totalVIPDiscount.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                      )}
                      
                      <div className="type-numeric flex justify-between text-slate-900 text-sm pt-4 border-t border-slate-200">
                          <span>Subtotal (Excl. GST)</span>
                          <span>${subtotal.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="type-numeric flex justify-between text-slate-500 text-[10px] uppercase tracking-widest">
                          <span>GST (15%)</span>
                          <span>${gst.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      
                      {/* ⚡ FIXED GRAND TOTAL: Baseline alignment, whitespace locked, clean font */}
                      <div className="flex justify-between items-baseline text-slate-900 font-black pt-4 border-t-2 border-slate-900 mt-2 gap-4">
                          <span className="uppercase tracking-widest text-sm md:text-base whitespace-nowrap">Grand Total</span>
                          <span className="type-numeric text-brand-neonBlue text-2xl md:text-3xl tracking-tight">${total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                  </div>
              </div>

              <div className="quote-terms type-body text-[11px] md:text-xs text-slate-500 border-t border-slate-100 pt-8 mt-auto md:mb-0 mb-28 print-avoid-break">
                  <h4 className="type-heading text-xs uppercase mb-2 text-slate-700">Terms & Conditions</h4>
                  <p className="mb-2 leading-relaxed">1. A 50% deposit is required to secure the date. Balance due 7 days prior to event. 2. Cancellation within 30 days forfeits deposit. 3. Power required within 5m of setup.</p>
              </div>
          </div>

          {isPublicView && <div className="hidden md:flex justify-center gap-4 my-8 print-hide">
            {isAcceptedQuote(quote) ? <a href={clientRoute('portal',quote.id)} className="text-brand-neonBlue">Next Step: Complete Event Blueprint →</a>
             : !isAcceptedQuote(quote) && quote.status !== 'REJECTED' && !isSupersededQuote(quote) && <>
              <Button onClick={handleApprove} disabled={expired || isApproving}>Approve &amp; Sign</Button>
              <Button onClick={handleReject}>Decline</Button>
             </>}
          </div>}

          {/* ⚡ MOBILE ACTION DRAWER: Also removed the blocky font here for consistency */}
          {isPublicView && quote?.status !== 'APPROVED' && quote?.status !== 'REJECTED' && !isSupersededQuote(quote) && (
              <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-950 p-4 border-t border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.8)] z-50 animate-slide-up flex flex-col gap-3 print-hide">
                  <div className="flex justify-between items-center px-1 items-baseline">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 whitespace-nowrap">Total Price</span>
                      <span className="text-brand-neonBlue font-black text-2xl">${total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex gap-3 mt-1">
                      <Button onClick={handleReject} className="flex-1 py-4 bg-slate-900 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-wider hover:bg-red-500/10 transition-colors">Decline</Button>
                      <Button onClick={handleApprove} disabled={calculationReviewRequired || expired || isApproving} className="flex-[2] py-4 bg-brand-neonBlue text-slate-950 border-none shadow-neon text-xs font-bold uppercase tracking-wider hover:bg-brand-neonBlue/90 transition-colors"><Check size={16} className="mr-2"/> Approve</Button>
                  </div>
              </div>
          )}
          </>
      )}
      {/* ⚡ HIDDEN DOM FOR 16:9 SLIDE PDF GENERATION ⚡ */}
      <div aria-hidden="true" className="absolute top-0 left-[-20000px] w-[1920px]">
          <style>
              {`
                  .print-slide-page * { 
                      animation: none !important; 
                      transition: none !important; 
                  }
              `}
          </style>
          <div id="presentation-print-container" className="quote-presentation font-sans w-[1920px] bg-slate-950 flex flex-col print-slide-page">
              {proposalSlides
                  .filter(slide => {
                      // Video cannot be represented faithfully in a static PDF; all
                      // other browser-deck slides retain the same authored sequence.
                      if (slide.type === 'PRODUCT' && slide.data) {
                          const product = slide.data.product;
                          const activeMedia = slide.data.slide?.url || slide.data.url || product?.image || product?.main_image;
                          if (activeMedia && (
                              activeMedia.toLowerCase().endsWith('.mp4') || 
                              activeMedia.toLowerCase().endsWith('.webm') || 
                              activeMedia.toLowerCase().endsWith('.mov') ||
                              activeMedia.includes('video')
                          )) {
                              return false;
                          }
                      }
                      return true; // Keep everything else
                  })
                  .map((slide, idx, arr) => (
                  <div key={idx} className="w-[1920px] h-[1079px] shrink-0 relative overflow-hidden" style={{ pageBreakAfter: idx === arr.length - 1 ? 'auto' : 'always' }}>
                      {slide.type === 'COVER' && <CoverSlide />}
                      {slide.type === 'INTRO' && <IntroSlide text={slide.data.text} />}
                      {slide.type === 'PRODUCT' && <ProductSlide data={slide.data} />}
                      {slide.type === 'PRICING' && <PricingSlide />}
                      {slide.type === 'ACCEPT' && <AcceptSlide />}
                      {slide.type === 'CUSTOM' && <CustomSlide data={slide.data} />}
                      {slide.type === 'PORTFOLIO_CTA' && <PortfolioCTASlide />}
                  </div>
              ))}
          </div>
      </div>

    </div>
  );
};
